import { ref, computed, type Ref } from "vue";
import type { ShopListInput, Chain, ShopResponse, ChainPaymentMethod } from "@/generated/graphql";
import { getWordList } from "@/common/graphql/expression";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import { getChainPaymentMethod } from "@/common/graphql/chain";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import type { DeliveryAreaInput, ChainSelectValue } from "../types";
import { AREA_LIST_FILE_NAME, REQUIRED_FIELD_LABEL_KEYS } from "../constants";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  chainListItems: Ref<Chain[]>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopListItems: Ref<ShopResponse[]>;
  shopListInput: ShopListInput;
  deliveryAreaInput: DeliveryAreaInput;
  paymentMethodItems: Ref<ChainPaymentMethod[]>;
  chainViewFlag: Ref<boolean>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListInit = ({
  errdialog,
  errMessage,
  chainListItems,
  chainSelectValues,
  shopListItems,
  shopListInput,
  deliveryAreaInput,
  paymentMethodItems,
  chainViewFlag,
}: UseAreaListInitParams) => {
  const wordList = ref<Record<string, string>>({});

  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return REQUIRED_FIELD_LABEL_KEYS.every((key) => key in wordList.value);
  });

  const runInitialLoad= async (): Promise<void> => {
    try {
      // 文言データを取得する
      wordList.value = await getWordList(
        localStorageGetChainCode(),
        AREA_LIST_FILE_NAME,
        localStorageGetCustomerCode()
      );

      if (chainViewFlag.value) {
        // 運用管理者：チェーン選択可能
        chainListItems.value = await getChainList(localStorageGetCustomerCode());
        chainSelectValues.value.chain_code = "";
        deliveryAreaInput.chain_code = "";
        shopListInput.chain_code = "";
      } else {
        // 一般ユーザ：自チェーン固定
        chainSelectValues.value.chain_code = localStorageGetChainCode();
        deliveryAreaInput.chain_code = localStorageGetChainCode();
        shopListInput.chain_code = localStorageGetChainCode();

        //店舗情報を取得する
        shopListItems.value = await getShopList(shopListInput);

        //決済方法の取得処理
        const paymentList = await getChainPaymentMethod(
          localStorageGetCustomerCode(),
          localStorageGetChainCode()
        );
        paymentMethodItems.value = (paymentList ?? []).filter((p): p is ChainPaymentMethod => p !== null && p !== undefined);
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
    }
  };

  return {
    wordList,
    isWordListReady,
    runInitialLoad,
  };
};
